import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Box, Card, CardContent, CircularProgress, Typography } from '@mui/material'
import GroupsIcon from '@mui/icons-material/Groups'
import ArticleIcon from '@mui/icons-material/Article'
import SearchIcon from '@mui/icons-material/Search'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ContactPhoneIcon from '@mui/icons-material/ContactPhone'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import { DateRangePicker, MetricCard } from '../../components/Performance'
import StatTable from '../../components/Performance/StatTable'
import SeriesChart from '../../components/Performance/SeriesChart'
import { useAuth } from '../../hooks/useAuth'
import {
  BreakdownRow,
  DETAIL_LABELS,
  DEVICE_LABELS,
  PAGE_TYPE_LABELS,
  avgPosition,
  contactsOf,
  fmtPct,
  pathLabel,
  periodFor,
  rate,
  refLabel,
  statsErrorMessage,
  sumSeries,
  trend,
  useStatsBreakdown,
  useStatsSeries,
} from '../../hooks/useStats'

const SITE = {}

export default function PerformanceSite() {
  const { isAdmin, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [days, setDays] = useState(30)

  useEffect(() => {
    if (!authLoading && !isAdmin) navigate('/performance/stats')
  }, [authLoading, isAdmin, navigate])

  const period = periodFor(days)
  const on = isAdmin
  const series = useStatsSeries(period.previousFrom, period.to, SITE, on)
  const pageTypes = useStatsBreakdown(period.from, period.to, 'page_type', SITE, 20, on)
  const guides = useStatsBreakdown(period.from, period.to, 'path', SITE, 25, on)
  const places = useStatsBreakdown(period.from, period.to, 'place', SITE, 25, on)
  const entities = useStatsBreakdown(period.from, period.to, 'entity', SITE, 30, on)
  const refs = useStatsBreakdown(period.from, period.to, 'ref', SITE, 20, on)
  const devices = useStatsBreakdown(period.from, period.to, 'device', SITE, 10, on)
  const details = useStatsBreakdown(period.from, period.to, 'detail', SITE, 50, on)

  if (authLoading || !isAdmin) return null

  const rows = series.data || []
  const current = rows.filter((r) => r.day >= period.from)
  const previous = rows.filter((r) => r.day <= period.previousTo)
  const now = sumSeries(current)
  const before = sumSeries(previous)
  const contactsNow = contactsOf(now)
  const totalSessions = (devices.data || []).reduce((s, r) => s + r.sessions, 0)
  const searchRows = (details.data || []).filter((r) => r.key.startsWith('search:') || r.key === 'page_view:no_results')
  const guideTargets = (details.data || []).filter((r) => r.key.startsWith('guide_click:'))
  const sponsorRows = (details.data || []).filter((r) => r.key.startsWith('feed_impression:') || r.key.startsWith('profile_click:'))

  type CardColor = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'
  const card = (title: string, value: number, previousValue: number, icon: React.ReactNode, color: CardColor, subtitle?: string) => (
    <Box sx={{ flex: '1 1 170px', minWidth: 170 }}>
      <MetricCard
        title={title}
        value={value}
        trend={trend(value, previousValue)}
        trendLabel="vs. forrige periode"
        subtitle={subtitle}
        icon={icon}
        color={color}
      />
    </Box>
  )

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <DateRangePicker value={days} onChange={setDays} />
      </Box>

      {series.error ? (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          {statsErrorMessage(series.error)}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Indtil det nye site er live, kommer tallene fra WordPress. Dér tælles en visning i listen, når kortet
          indlæses, også hvis det aldrig kommer frem på skærmen, og sidevisninger og søgninger tælles slet ikke.
        </Alert>
      )}

      {series.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
            {card('Besøg', now.sessions, before.sessions, <GroupsIcon sx={{ fontSize: 20 }} />, 'primary', 'Skøn uden cookies')}
            {card('Sidevisninger', now.page_views, before.page_views, <ArticleIcon sx={{ fontSize: 20 }} />, 'info')}
            {card('Søgninger', now.searches, before.searches, <SearchIcon sx={{ fontSize: 20 }} />, 'secondary')}
            {card('Vist i lister', now.impressions, before.impressions, <FormatListBulletedIcon sx={{ fontSize: 20 }} />, 'secondary',
              `Klikrate ${fmtPct(rate(now.profile_clicks, now.impressions))}`)}
            {card('Profilvisninger', now.profile_views, before.profile_views, <VisibilityIcon sx={{ fontSize: 20 }} />, 'primary')}
            {card('Kontaktklik', contactsNow, contactsOf(before), <ContactPhoneIcon sx={{ fontSize: 20 }} />, 'success',
              `${fmtPct(rate(contactsNow - now.listing_contacts, now.profile_views))} af profilvisninger`)}
            {card('Fra guides til kataloget', now.guide_clicks, before.guide_clicks, <MenuBookIcon sx={{ fontSize: 20 }} />, 'warning')}
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <SeriesChart
                title="Udvikling dag for dag"
                rows={current}
                metrics={[
                  { key: 'sessions', label: 'Besøg', color: '#5c4b99', value: (r) => r.sessions },
                  { key: 'page_views', label: 'Sidevisninger', color: '#0284c7', value: (r) => r.page_views },
                  { key: 'profile_views', label: 'Profilvisninger', color: '#7c3aed', value: (r) => r.profile_views },
                  { key: 'contacts', label: 'Kontaktklik', color: '#16a34a', value: (r) => contactsOf(r) },
                ]}
              />
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
            <Card sx={{ flex: '1 1 520px', minWidth: 300 }}>
              <CardContent>
                <StatTable
                  title="Sidetyper"
                  subtitle="Indgange er besøg, der startede på sidetypen."
                  rows={(pageTypes.data || []).filter((r) => r.events + r.sessions > 0)}
                  rowKey={(r) => r.key}
                  loading={pageTypes.isLoading}
                  initialSort="page_views"
                  columns={[
                    { key: 'label', label: 'Sidetype', align: 'left', value: (r) => PAGE_TYPE_LABELS[r.key] ?? r.key },
                    { key: 'page_views', label: 'Sidevisninger', value: (r) => r.page_views },
                    { key: 'sessions', label: 'Indgange', value: (r) => r.sessions },
                    { key: 'impressions', label: 'Vist i lister', value: (r) => r.impressions },
                    { key: 'profile_clicks', label: 'Klik på profil', value: (r) => r.profile_clicks },
                    { key: 'contacts', label: 'Kontaktklik', value: (r) => r.contacts },
                  ]}
                />
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 360px', minWidth: 300 }}>
              <CardContent>
                <StatTable
                  title="Søgninger"
                  rows={searchRows}
                  rowKey={(r) => r.key}
                  loading={details.isLoading}
                  initialSort="count"
                  empty="Ingen søgninger målt i perioden"
                  columns={[
                    { key: 'label', label: 'Hvad skete der', align: 'left', value: (r) => DETAIL_LABELS[r.key] ?? r.key },
                    { key: 'count', label: 'Antal', value: (r) => r.events },
                  ]}
                />
              </CardContent>
            </Card>
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <StatTable
                title="Bedemænd og kæder"
                subtitle="Tryk på en række for at se profilens egne tal."
                rows={(entities.data || []).filter((r) => r.events > 0)}
                rowKey={(r) => r.key}
                loading={entities.isLoading}
                initialSort="impressions"
                onRowClick={(r) => navigate(`/performance/stats?id=${r.key}`)}
                columns={[
                  { key: 'label', label: 'Navn', align: 'left', value: (r) => r.label },
                  { key: 'impressions', label: 'Vist i lister', value: (r) => r.impressions },
                  { key: 'profile_clicks', label: 'Klik på profil', value: (r) => r.profile_clicks },
                  {
                    key: 'ctr',
                    label: 'Klikrate',
                    value: (r) => rate(r.profile_clicks, r.impressions),
                    format: (v) => fmtPct(v),
                  },
                  {
                    key: 'position',
                    label: 'Ø placering',
                    value: (r) => avgPosition(r),
                    format: (v) => (v === null ? '-' : v.toLocaleString('da-DK', { maximumFractionDigits: 1 })),
                  },
                  { key: 'profile_views', label: 'Profilvisninger', value: (r) => r.profile_views },
                  { key: 'contacts', label: 'Kontaktklik', value: (r) => r.contacts },
                ]}
              />
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
            <Card sx={{ flex: '1 1 480px', minWidth: 300 }}>
              <CardContent>
                <StatTable<BreakdownRow>
                  title="Mest læste guides og sider"
                  rows={(guides.data || []).filter((r) => r.page_views + r.sessions > 0)}
                  rowKey={(r) => r.key}
                  loading={guides.isLoading}
                  initialSort="page_views"
                  columns={[
                    { key: 'label', label: 'Side', align: 'left', value: (r) => pathLabel(r) },
                    { key: 'page_views', label: 'Visninger', value: (r) => r.page_views },
                    { key: 'sessions', label: 'Indgange', value: (r) => r.sessions },
                    {
                      key: 'guide_clicks',
                      label: 'Klik til kataloget',
                      help: 'Klik fra siden videre til en lokationsside, en profil, søgningen eller byoversigten',
                      value: (r) => r.guide_clicks,
                    },
                    {
                      key: 'guide_rate',
                      label: 'Andel',
                      value: (r) => rate(r.guide_clicks, r.page_views),
                      format: (v) => fmtPct(v, 0),
                    },
                  ]}
                />
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 480px', minWidth: 300 }}>
              <CardContent>
                <StatTable
                  title="Lokationer"
                  subtitle="Lokationssider, og adressesøgninger samlet på nærmeste by."
                  rows={places.data || []}
                  rowKey={(r) => r.key}
                  loading={places.isLoading}
                  initialSort="page_views"
                  columns={[
                    { key: 'label', label: 'By eller område', align: 'left', value: (r) => r.label },
                    { key: 'page_views', label: 'Sidevisninger', value: (r) => r.page_views },
                    { key: 'sessions', label: 'Indgange', value: (r) => r.sessions },
                    { key: 'impressions', label: 'Vist i lister', value: (r) => r.impressions },
                    { key: 'profile_clicks', label: 'Klik på profil', value: (r) => r.profile_clicks },
                  ]}
                />
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
            <Card sx={{ flex: '1 1 380px', minWidth: 300 }}>
              <CardContent>
                <StatTable
                  title="Trafikkilder"
                  subtitle="Hvor besøget startede."
                  rows={(refs.data || []).filter((r) => r.sessions > 0 && !r.key.startsWith('internal_'))}
                  rowKey={(r) => r.key}
                  loading={refs.isLoading}
                  initialSort="sessions"
                  columns={[
                    { key: 'label', label: 'Kilde', align: 'left', value: (r) => refLabel(r.key) },
                    { key: 'sessions', label: 'Besøg', value: (r) => r.sessions },
                    {
                      key: 'share',
                      label: 'Andel',
                      value: (r) => rate(r.sessions, now.sessions),
                      format: (v) => fmtPct(v, 0),
                    },
                  ]}
                />
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 300px', minWidth: 280 }}>
              <CardContent>
                <StatTable
                  title="Enheder"
                  rows={(devices.data || []).filter((r) => r.sessions + r.events > 0)}
                  rowKey={(r) => r.key}
                  loading={devices.isLoading}
                  initialSort="sessions"
                  columns={[
                    { key: 'label', label: 'Enhed', align: 'left', value: (r) => DEVICE_LABELS[r.key] ?? r.key },
                    { key: 'sessions', label: 'Besøg', value: (r) => r.sessions },
                    {
                      key: 'share',
                      label: 'Andel',
                      value: (r) => rate(r.sessions, totalSessions),
                      format: (v) => fmtPct(v, 0),
                    },
                    { key: 'contacts', label: 'Kontaktklik', value: (r) => r.contacts },
                  ]}
                />
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 300px', minWidth: 280 }}>
              <CardContent>
                <StatTable
                  title="Guides sender videre til"
                  rows={guideTargets}
                  rowKey={(r) => r.key}
                  loading={details.isLoading}
                  initialSort="count"
                  empty="Ingen klik målt endnu"
                  columns={[
                    { key: 'label', label: 'Mål', align: 'left', value: (r) => DETAIL_LABELS[r.key] ?? r.key },
                    { key: 'count', label: 'Klik', value: (r) => r.events },
                  ]}
                />
                {sponsorRows.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <StatTable
                      title="Sponsoreret eller ej"
                      rows={sponsorRows}
                      rowKey={(r) => r.key}
                      initialSort="count"
                      columns={[
                        { key: 'label', label: 'Type', align: 'left', value: (r) => DETAIL_LABELS[r.key] ?? r.key },
                        { key: 'count', label: 'Antal', value: (r) => r.events },
                      ]}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Besøg tælles uden cookies og uden at gemme IP-adresser. En genindlæst side eller en ny fane tæller som et nyt
            besøg, så tallet er et skøn. Trafik fra robotter og fra localhost er sorteret fra.
          </Typography>
        </>
      )}
    </Box>
  )
}
